import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

export type CarouselPrize = Database['public']['Tables']['carousel_prizes']['Row'];
export type CarouselPrizeInsert = Database['public']['Tables']['carousel_prizes']['Insert'];
export type CarouselPrizeUpdate = Database['public']['Tables']['carousel_prizes']['Update'];
export type CarouselWin = Database['public']['Tables']['carousel_wins']['Row'];

export type CarouselWinWithPrize = CarouselWin & {
  prize?: Pick<CarouselPrize, 'id' | 'name' | 'image_url' | 'is_big_prize'> | null;
  staff?: { id: string; full_name: string | null } | null;
};

export type SpinResult = {
  win_id: string;
  prize_id: string;
  prize_name: string;
  image_url: string | null;
  is_big_prize: boolean;
  remaining_quantity: number;
  total_quantity: number;
  customer_first_name: string;
  customer_last_name: string;
};

export type PrizeUploadResult = {
  url: string;
  path: string;
  success: boolean;
  error?: string;
};

export type PrizeStats = {
  remaining: number;
  given: number;
  bigRemaining: number;
  activePrizeCount: number;
};

const BUCKET = 'carousel-prizes';
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const VALID_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

function isValidImageFile(file: File): boolean {
  return VALID_IMAGE_TYPES.includes(file.type);
}

export async function listCarouselPrizes(companyId: string): Promise<CarouselPrize[]> {
  const { data, error } = await supabase
    .from('carousel_prizes')
    .select('*')
    .eq('company_id', companyId)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data ?? [];
}

export async function listActivePrizesWithStock(companyId: string): Promise<CarouselPrize[]> {
  const { data, error } = await supabase
    .from('carousel_prizes')
    .select('*')
    .eq('company_id', companyId)
    .eq('is_active', true)
    .gt('remaining_quantity', 0)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data ?? [];
}

export function computePrizeStats(prizes: CarouselPrize[]): PrizeStats {
  return prizes.reduce(
    (acc, prize) => {
      if (!prize.is_active) return acc;
      const given = prize.total_quantity - prize.remaining_quantity;
      acc.remaining += prize.remaining_quantity;
      acc.given += given;
      acc.activePrizeCount += 1;
      if (prize.is_big_prize) {
        acc.bigRemaining += prize.remaining_quantity;
      }
      return acc;
    },
    { remaining: 0, given: 0, bigRemaining: 0, activePrizeCount: 0 } as PrizeStats
  );
}

export async function createCarouselPrize(
  companyId: string,
  input: {
    name: string;
    total_quantity: number;
    is_big_prize?: boolean;
    image_url?: string | null;
    image_path?: string | null;
    sort_order?: number;
  }
): Promise<CarouselPrize> {
  const payload: CarouselPrizeInsert = {
    company_id: companyId,
    name: input.name.trim(),
    total_quantity: input.total_quantity,
    remaining_quantity: input.total_quantity,
    is_big_prize: input.is_big_prize ?? false,
    image_url: input.image_url ?? null,
    image_path: input.image_path ?? null,
    sort_order: input.sort_order ?? 0,
    is_active: true,
  };

  const { data, error } = await supabase
    .from('carousel_prizes')
    .insert(payload)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateCarouselPrize(
  prizeId: string,
  updates: {
    name?: string;
    is_big_prize?: boolean;
    is_active?: boolean;
    sort_order?: number;
    image_url?: string | null;
    image_path?: string | null;
    /** When increasing total, remaining is increased by the same delta. Decreasing total clamps remaining. */
    total_quantity?: number;
    current?: Pick<CarouselPrize, 'total_quantity' | 'remaining_quantity'>;
  }
): Promise<CarouselPrize> {
  const patch: CarouselPrizeUpdate = {};

  if (updates.name !== undefined) patch.name = updates.name.trim();
  if (updates.is_big_prize !== undefined) patch.is_big_prize = updates.is_big_prize;
  if (updates.is_active !== undefined) patch.is_active = updates.is_active;
  if (updates.sort_order !== undefined) patch.sort_order = updates.sort_order;
  if (updates.image_url !== undefined) patch.image_url = updates.image_url;
  if (updates.image_path !== undefined) patch.image_path = updates.image_path;

  if (updates.total_quantity !== undefined && updates.current) {
    const nextTotal = updates.total_quantity;
    const given = updates.current.total_quantity - updates.current.remaining_quantity;
    if (nextTotal < given) {
      throw new Error('total_below_given');
    }
    patch.total_quantity = nextTotal;
    patch.remaining_quantity = nextTotal - given;
  }

  const { data, error } = await supabase
    .from('carousel_prizes')
    .update(patch)
    .eq('id', prizeId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteCarouselPrize(prizeId: string, imagePath?: string | null): Promise<void> {
  const { error } = await supabase.from('carousel_prizes').delete().eq('id', prizeId);
  if (error) throw error;

  if (imagePath) {
    await supabase.storage.from(BUCKET).remove([imagePath]);
  }
}

export async function uploadPrizeImage(companyId: string, file: File): Promise<PrizeUploadResult> {
  if (!isValidImageFile(file)) {
    return {
      url: '',
      path: '',
      success: false,
      error: 'Geçersiz dosya türü. Sadece JPG, PNG ve WebP kabul edilir.',
    };
  }

  if (file.size > MAX_IMAGE_BYTES) {
    return {
      url: '',
      path: '',
      success: false,
      error: 'Dosya boyutu çok büyük. Maksimum 5MB.',
    };
  }

  const timestamp = Date.now();
  const randomId = Math.random().toString(36).substring(2);
  const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
  const fileName = `${timestamp}_${randomId}.${fileExtension}`;
  const filePath = `${companyId}/${fileName}`;

  const { error } = await supabase.storage.from(BUCKET).upload(filePath, file, {
    cacheControl: '3600',
    upsert: false,
  });

  if (error) {
    return {
      url: '',
      path: '',
      success: false,
      error: `Yükleme başarısız: ${error.message}`,
    };
  }

  const {
    data: { publicUrl },
  } = supabase.storage.from(BUCKET).getPublicUrl(filePath);

  return {
    url: publicUrl,
    path: filePath,
    success: true,
  };
}

export async function listCarouselWins(companyId: string): Promise<CarouselWinWithPrize[]> {
  const { data, error } = await supabase
    .from('carousel_wins')
    .select(`
      *,
      prize:carousel_prizes!carousel_wins_prize_id_fkey(id, name, image_url, is_big_prize),
      staff:user_profiles!carousel_wins_won_by_user_id_fkey(id, full_name)
    `)
    .eq('company_id', companyId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data as CarouselWinWithPrize[]) ?? [];
}

export async function spinCarouselPrize(
  companyId: string,
  firstName: string,
  lastName: string
): Promise<SpinResult> {
  const { data, error } = await supabase.rpc('spin_carousel_prize', {
    p_company_id: companyId,
    p_first_name: firstName.trim(),
    p_last_name: lastName.trim(),
  });

  if (error) throw error;
  return data as SpinResult;
}

export function mapSpinError(error: unknown): string {
  const message =
    error && typeof error === 'object' && 'message' in error
      ? String((error as { message: string }).message)
      : String(error ?? '');

  if (message.includes('no_prizes_remaining')) {
    return 'Dağıtılacak ödül kalmadı.';
  }
  if (message.includes('customer_name_required')) {
    return 'Lütfen müşteri adı ve soyadını girin.';
  }
  if (message.includes('unauthorized_company')) {
    return 'Bu işlem için yetkiniz yok.';
  }
  if (message.includes('total_below_given')) {
    return 'Toplam adet, verilmiş ödül sayısından az olamaz.';
  }
  return message || 'Beklenmeyen bir hata oluştu.';
}
